"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Worksheet = void 0;
const Contract = require("@tableau/api-external-contract-js");
const api_external_contract_js_1 = require("@tableau/api-external-contract-js");
const api_internal_contract_js_1 = require("@tableau/api-internal-contract-js");
const api_shared_js_1 = require("@tableau/api-shared-js");
const FilterChangedEvent_1 = require("../Events/FilterChangedEvent");
const MarksSelectedEvent_1 = require("../Events/MarksSelectedEvent");
const SummaryDataChangedEvent_1 = require("../Events/SummaryDataChangedEvent");
const WorksheetFormattingChangedEvent_1 = require("../Events/WorksheetFormattingChangedEvent");
const ServiceRegistryUtil_1 = require("../Services/ServiceRegistryUtil");
const Dashboard_1 = require("./Dashboard");
const Sheet_1 = require("./Sheet");
class Worksheet extends Sheet_1.Sheet {
    constructor(_worksheetImpl) {
        super(_worksheetImpl);
        this._worksheetImpl = _worksheetImpl;
        // Call to initialize events and then call down to the event listener manager to handle things
        this.initializeEvents().forEach((e) => this.addNewEventType(e));
    }
    get parentDashboard() {
        // This should never happen for Extensions if the DashboardImpls are created correctly
        if (this._worksheetImpl.parentDashboard == null) {
            throw new api_shared_js_1.TableauError(Contract.SharedErrorCodes.ImplementationError, `Parent dashboard not implemented`);
        }
        return new Dashboard_1.Dashboard(this._worksheetImpl.parentDashboard);
    }
    get backgroundColor() {
        if (this._worksheetImpl.backgroundColor == null) {
            throw new api_shared_js_1.TableauError(Contract.SharedErrorCodes.ImplementationError, `Worksheet background color is not available`);
        }
        return this._worksheetImpl.backgroundColor;
    }
    get formatting() {
        if (this._worksheetImpl.formatting == null) {
            throw new api_shared_js_1.TableauError(Contract.SharedErrorCodes.ImplementationError, `Worksheet formatting information is not available`);
        }
        return this._worksheetImpl.formatting;
    }
    /**
     * Helper method which goes through and registers each event type this impl knows about
     * with the NotificationService. It returns an array of SingleEventManager objects which
     * can then be passed to an EventListenerManager to handle user registration / unregistration.
     *
     * @param {Worksheet} worksheet The worksheet object which will be included with the event notifications
     * @returns {Array<SingleEventManager>} Collection of event managers to pass to an EventListenerManager
     */
    initializeEvents() {
        const results = new Array();
        let notificationService;
        try {
            notificationService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("notification-service" /* ServiceNames.Notification */);
        }
        catch (e) {
            // If we don't have this service registered, just return
            return results;
        }
        // Initialize all of the event managers we'll need (one for each event type)
        const marksEvent = new api_shared_js_1.SingleEventManagerImpl(api_external_contract_js_1.TableauEventType.MarkSelectionChanged);
        notificationService.registerHandler(api_internal_contract_js_1.NotificationId.SelectedMarksChanged, (model) => {
            const visualId = model;
            return this.visualIdsAreEqual(visualId, this._worksheetImpl.visualId);
        }, () => marksEvent.triggerEvent(() => new MarksSelectedEvent_1.MarksSelectedEvent(this)));
        const filterEvent = new api_shared_js_1.SingleEventManagerImpl(api_external_contract_js_1.TableauEventType.FilterChanged);
        notificationService.registerHandler(api_internal_contract_js_1.NotificationId.FilterChanged, (model) => {
            const filterEventResponse = model;
            return this._worksheetImpl.visualId.worksheet === filterEventResponse.visualId.worksheet;
        }, (event) => {
            filterEvent.triggerEvent(() => new FilterChangedEvent_1.FilterChangedEvent(this, event.fieldName, event.fieldId));
        });
        const summaryDataEvent = new api_shared_js_1.SingleEventManagerImpl(api_external_contract_js_1.TableauEventType.SummaryDataChanged);
        notificationService.registerHandler(api_internal_contract_js_1.NotificationId.SummaryDataChanged, (model) => {
            const summaryDataEventResponse = model;
            return this._worksheetImpl.visualId.worksheet === summaryDataEventResponse.visualId.worksheet;
        }, (event) => {
            summaryDataEvent.triggerEvent(() => new SummaryDataChangedEvent_1.SummaryDataChangedEvent(this));
        });
        const worksheetFormattingChangedEvent = new api_shared_js_1.SingleEventManagerImpl(api_external_contract_js_1.TableauEventType.WorksheetFormattingChanged);
        notificationService.registerHandler(api_internal_contract_js_1.NotificationId.WorksheetFormattingChanged, (model) => {
            const worksheetFormattingChangedEvent = model;
            return this._worksheetImpl.visualId.worksheet === worksheetFormattingChangedEvent.visualId.worksheet;
        }, (eventFormatting) => {
            worksheetFormattingChangedEvent.triggerEvent(() => new WorksheetFormattingChangedEvent_1.WorksheetFormattingChangedEvent(this, eventFormatting));
        });
        results.push(marksEvent);
        results.push(filterEvent);
        results.push(summaryDataEvent);
        results.push(worksheetFormattingChangedEvent);
        return results;
    }
    applyFilterAsync(fieldName, values, updateType, options) {
        return this._worksheetImpl.applyFilterAsync(fieldName, values, updateType, options);
    }
    applyRangeFilterAsync(fieldName, filterOptions) {
        return this._worksheetImpl.applyRangeFilterAsync(fieldName, filterOptions);
    }
    applyHierarchicalFilterAsync(fieldName, values, updateType, options) {
        return this._worksheetImpl.applyHierarchicalFilterAsync(fieldName, values, updateType, options);
    }
    applyRelativeDateFilterAsync(fieldName, options) {
        return this._worksheetImpl.applyRelativeDateFilterAsync(fieldName, options);
    }
    clearFilterAsync(fieldName) {
        return this._worksheetImpl.clearFilterAsync(fieldName);
    }
    getDataSourcesAsync() {
        return this._worksheetImpl.getDataSourcesAsync();
    }
    getFiltersAsync() {
        return this._worksheetImpl.getFiltersAsync();
    }
    getSelectedMarksAsync() {
        return this._worksheetImpl.getSelectedMarksAsync();
    }
    getHighlightedMarksAsync() {
        return this._worksheetImpl.getHighlightedMarksAsync();
    }
    getSummaryDataAsync(options) {
        return this._worksheetImpl.getSummaryDataAsync(options);
    }
    getSummaryDataReaderAsync(pageRowCount, options) {
        return this._worksheetImpl.getSummaryDataReaderAsync(pageRowCount, options);
    }
    getSummaryColumnsInfoAsync() {
        return this._worksheetImpl.getSummaryColumnsInfoAsync();
    }
    getUnderlyingDataAsync(options) {
        console.warn('Worksheet.getUnderlyingDataAsync is deprecated. Please use ' +
            'Worksheet.getUnderlyingTablesAsync and Worksheet.getUnderlyingTableDataAsync');
        return this._worksheetImpl.getUnderlyingDataAsync(options);
    }
    getUnderlyingTablesAsync() {
        return this._worksheetImpl.getUnderlyingTablesAsync();
    }
    getUnderlyingTableDataAsync(logicalTableId, options) {
        return this._worksheetImpl.getUnderlyingTableDataAsync(logicalTableId, options);
    }
    getUnderlyingTableDataReaderAsync(logicalTableId, pageRowCount, options) {
        return this._worksheetImpl.getUnderlyingTableDataReaderAsync(logicalTableId, pageRowCount, options);
    }
    getVisualSpecificationAsync() {
        return this._worksheetImpl.getVisualSpecificationAsync();
    }
    addMarksCardFieldsAsync(marksCardIndex, encodingType, columns, startIndex) {
        return this._worksheetImpl.addMarksCardFieldsAsync(marksCardIndex, encodingType, columns, startIndex);
    }
    moveMarksCardFieldAsync(marksCardIndex, fromIndex, toIndex, fieldCount = 1) {
        return this._worksheetImpl.moveMarksCardFieldAsync(marksCardIndex, fromIndex, toIndex, fieldCount);
    }
    spliceMarksCardFieldsAsync(marksCardIndex, encodingType, startIndex, deleteCount, columns) {
        return this._worksheetImpl.spliceMarksCardFieldsAsync(marksCardIndex, encodingType, startIndex, deleteCount, columns);
    }
    clearSelectedMarksAsync() {
        return this._worksheetImpl.clearSelectedMarksAsync();
    }
    selectMarksByIDAsync(marksInfo, updateType) {
        return this._worksheetImpl.selectMarksByIdAsync(marksInfo, updateType);
    }
    selectMarksByValueAsync(selections, selectionUpdateType) {
        return this._worksheetImpl.selectMarksByValueAsync(selections, selectionUpdateType);
    }
    selectMarksByIdAsync(selections, selectionUpdateType) {
        return this._worksheetImpl.selectMarksByIdAsync(selections, selectionUpdateType);
    }
    annotateMarkAsync(mark, annotationText) {
        return this._worksheetImpl.annotateMarkAsync(mark, annotationText);
    }
    getAnnotationsAsync() {
        return this._worksheetImpl.getAnnotationsAsync();
    }
    removeAnnotationAsync(annotation) {
        return this._worksheetImpl.removeAnnotationAsync(annotation);
    }
    hoverTupleAsync(hoveredTuple, tooltip, allowHoverActions = true) {
        return this._worksheetImpl.hoverTupleAsync(hoveredTuple, tooltip, allowHoverActions);
    }
    selectTuplesAsync(selectedTuples, selectOption, tooltip) {
        return this._worksheetImpl.selectTuplesAsync(selectedTuples, selectOption, tooltip);
    }
    getTooltipTextAsync(tupleId) {
        return this._worksheetImpl.getTooltipTextAsync(tupleId);
    }
    leaveMarkNavigationAsync() {
        return this._worksheetImpl.leaveMarkNavigationAsync();
    }
    editAliasesDialogAsync(fieldName) {
        return this._worksheetImpl.editAliasesDialogAsync(fieldName);
    }
    visualIdsAreEqual(a, b) {
        return (a &&
            b &&
            a.worksheet === b.worksheet &&
            a.dashboard === b.dashboard &&
            a.storyboard === b.storyboard &&
            a.storyPointID === b.storyPointID);
    }
}
exports.Worksheet = Worksheet;
//# sourceMappingURL=Worksheet.js.map